"use client";

/**
 * Login — Yellow Business Intelligence
 *
 * Aligned with the OS 2.0 design system: pure white ground, OS green
 * primary CTA, ink text, electric-blue focus ring. Headline and slug
 * typography route through Jost via the --font-display token so the
 * unauthenticated surface reads in the same editorial voice as the
 * rest of the OS.
 */

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, LogIn, Shield } from "lucide-react";

// ─── OS 2.0 palette ──────────────────────
const PRAXETI_WHITE   = "#FFFFFF";
const MIDNIGHT_MIRAGE = "#0A0A0A";
const PICTURE_GREEN   = "#00C853";
const SPRING_LIME     = "#0057FF";
const MANTIS          = "#00C853";
const DANGER          = "#B8001F";

export default function LoginPage() {
  const router = useRouter();
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // OTP 2FA gate. Defaults to OFF — when REQUIRE_OTP_FOR_ALL_LOGINS
    // (exposed to the client via NEXT_PUBLIC_REQUIRE_OTP_FOR_ALL_LOGINS)
    // is "true", we route every credentials check through the
    // admin-approval flow. Trusted devices skip the OTP step
    // server-side.
    const otpEnabled =
      (process.env.NEXT_PUBLIC_REQUIRE_OTP_FOR_ALL_LOGINS || "").toLowerCase() ===
      "true";

    if (otpEnabled) {
      try {
        const res = await fetch("/api/auth/otp/request", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json().catch(() => ({} as Record<string, unknown>));

        if (!res.ok) {
          setLoading(false);
          if (res.status === 401) {
            setError("Access denied. Check your credentials and try again.");
          } else if (res.status === 429) {
            setError(
              String(data?.error) ||
                "Too many attempts. Wait a few minutes and try again.",
            );
          } else if (res.status === 503) {
            setError(
              "We couldn't deliver the approval code right now. Try again, or contact Shrey.",
            );
          } else {
            setError(String(data?.error) || "Sign-in failed. Try again.");
          }
          return;
        }

        if (data?.trustedDevice) {
          // Trusted device — proceed straight to NextAuth signIn.
          const result = await signIn("credentials", {
            email, password, redirect: false,
          });
          setLoading(false);
          if (result?.error) {
            setError("Access denied. Check your credentials and try again.");
            return;
          }
          router.push(typeof data?.redirectUrl === "string" ? data.redirectUrl : "/");
          router.refresh();
          return;
        }

        // New device — stash credentials in sessionStorage for the OTP
        // step to finalise, then redirect.
        try {
          sessionStorage.setItem(
            "yos.otp.challenge",
            JSON.stringify({
              challengeId: data?.challengeId,
              email,
              password,
              expiresAt: data?.expiresAt,
              maskedDestination: data?.maskedDestination,
            }),
          );
        } catch {
          // sessionStorage may be unavailable (private mode); proceed
          // anyway — the OTP page will surface a "sign in again" error.
        }
        setLoading(false);
        router.push(`/login/otp?challenge=${encodeURIComponent(String(data?.challengeId || ""))}`);
        return;
      } catch (err) {
        console.error("[login] OTP request failed:", err);
        setLoading(false);
        setError("Network error. Try again.");
        return;
      }
    }

    // Legacy direct sign-in (OTP gate disabled).
    const result = await signIn("credentials", {
      email, password, redirect: false,
    });

    setLoading(false);

    if (result?.error) {
      setError("Access denied. Check your credentials and try again.");
    } else {
      router.push("/");
      router.refresh();
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{
        // Push 30 · Yellow OS 2.0 · login ground is pure white with a
        // single subtle electric-blue veil behind the auth card.
        background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)",
      }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(0,87,255,0.06), transparent 70%)" }}
        />
      </div>

      <div className="relative w-full max-w-sm">

        {/* Logo lockup — canonical Yellow agency trademark (amber disc,
            navy script "Yellow®") + editorial wordmark below. */}
        <div className="text-center mb-8 animate-slide-up">
          <div className="flex justify-center mb-5">
            <img
              src="/yellow-favicon.png?v=3"
              alt="Yellow"
              className="h-16 w-16 rounded-full object-cover"
              style={{
                boxShadow: `
                  0 6px 24px rgba(245,166,35,0.28),
                  0 1px 2px rgba(0,0,0,0.10),
                  0 0 0 4px rgba(255,255,255,0.92)
                `,
              }}
            />
          </div>
          <h1
            className="font-display text-[1.75rem] font-semibold leading-tight"
            style={{ color: MIDNIGHT_MIRAGE, letterSpacing: "-0.015em" }}
          >
            Yellow
          </h1>
          <p
            className="text-[0.62rem] font-semibold uppercase mt-2"
            style={{
              fontFamily: "var(--font-display)",
              color: "rgba(0,0,0,0.55)",
              letterSpacing: "0.28em",
            }}
          >
            Business Intelligence System
          </p>
        </div>

        {/* Push 30 · OS 2.0 login card · pure white with subtle shadow. */}
        <div
          className="rounded-2xl p-8 animate-slide-up animate-delay-100"
          style={{
            background: "#FFFFFF",
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 20px 25px rgba(0,0,0,0.06), 0 10px 10px rgba(0,0,0,0.02)",
          }}
        >
          {/* Security badge */}
          <div className="flex items-center gap-1.5 mb-5">
            <Shield size={12} style={{ color: PICTURE_GREEN }} />
            <span
              className="text-[0.6rem] font-semibold uppercase"
              style={{
                fontFamily: "var(--font-display)",
                letterSpacing: "0.22em",
                color: "rgba(0,0,0,0.50)",
              }}
            >
              Secure sign in
            </span>
          </div>

          {error && (
            <div
              className="mb-4 px-3 py-2.5 rounded-lg text-xs"
              style={{
                background: "rgba(184,0,31,0.06)",
                border: "1px solid rgba(184,0,31,0.20)",
                color: DANGER,
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                className="block text-[0.6rem] font-semibold uppercase mb-1.5"
                style={{
                  fontFamily: "var(--font-display)",
                  letterSpacing: "0.22em",
                  color: "rgba(0,0,0,0.55)",
                }}
              >
                Email
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                placeholder="your@buildyellow.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                style={{
                  borderColor: "rgba(0,87,255,0.22)",
                  color: MIDNIGHT_MIRAGE,
                }}
                onFocus={e => {
                  e.target.style.borderColor = `rgba(0,87,255,0.55)`;
                  e.target.style.boxShadow   = `0 0 0 3px rgba(0,87,255,0.16)`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = "rgba(0,87,255,0.22)";
                  e.target.style.boxShadow   = "none";
                }}
              />
            </div>

            <div>
              <label
                className="block text-[0.6rem] font-semibold uppercase mb-1.5"
                style={{
                  fontFamily: "var(--font-display)",
                  letterSpacing: "0.22em",
                  color: "rgba(0,0,0,0.55)",
                }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full rounded-lg border bg-white px-3 py-2.5 pr-10 text-sm focus:outline-none transition-all"
                  style={{
                    borderColor: "rgba(0,87,255,0.22)",
                    color: MIDNIGHT_MIRAGE,
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = `rgba(0,87,255,0.55)`;
                    e.target.style.boxShadow   = `0 0 0 3px rgba(0,87,255,0.16)`;
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = "rgba(0,87,255,0.22)";
                    e.target.style.boxShadow   = "none";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: "rgba(0,0,0,0.45)" }}
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-all mt-2"
              style={{
                // Push 30 · OS 2.0 · solid green primary button.
                background: "#00C853",
                color: "#FFFFFF",
                boxShadow: "0 1px 3px rgba(0,200,83,0.32), 0 4px 12px rgba(0,200,83,0.20)",
                letterSpacing: "0.02em",
              }}
            >
              {loading ? (
                <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn size={15} />
                  Sign in
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer — uniform with the rest of the OS */}
        <div className="text-center mt-6 animate-slide-up animate-delay-200 space-y-1">
          <div
            className="text-[0.62rem] font-semibold uppercase"
            style={{
              fontFamily: "var(--font-display)",
              letterSpacing: "0.24em",
              color: "rgba(0,0,0,0.50)",
            }}
          >
            Yellow · Business Intelligence System
          </div>
          <div className="text-2xs leading-relaxed" style={{ color: "rgba(0,0,0,0.42)" }}>
            Proprietary platform of{" "}
            <span className="font-semibold" style={{ color: "rgba(0,0,0,0.62)" }}>
              Build Yellow Pvt. Ltd.
            </span>{" "}
            · All IP rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}
